/**
 * Captura client-side da vista corrente da mesa como PNG (E36.F4). // ux-neutrality:allow doc-comment
 *
 * Compõe num canvas offscreen exatamente o que a perita vê no viewport da // ux-neutrality:allow doc-comment
 * contestada: as imagens (com o CSS filter corrente — ex. cores invertidas), // ux-neutrality:allow doc-comment
 * o canvas da mesa Sobrepor & alinhar (transform/opacidade/anotações já // ux-neutrality:allow doc-comment
 * rasterizados pelo Fabric no lower-canvas) e os overlays SVG (marcações do // ux-neutrality:allow doc-comment
 * sistema, anotações manuais, grade, régua), cada um recortado pelo seu // ux-neutrality:allow doc-comment
 * viewport de zoom/pan. APRESENTAÇÃO-only (ADR-0040 §D): a captura é imagem // ux-neutrality:allow doc-comment
 * para o laudo, nunca medida. // ux-neutrality:allow doc-comment
 *
 * Exclusões deliberadas: `.upper-canvas` do Fabric (handles de seleção não // ux-neutrality:allow doc-comment
 * pertencem ao laudo), SVGs aninhados (o root já os rasteriza) e `.capture-exclude` // ux-neutrality:allow doc-comment
 * — a camada das marcações da triagem (E47.F3), que é apoio visual da raia // ux-neutrality:allow doc-comment
 * isolada e NÃO pode entrar no documento pericial enquanto o perito não adotar // ux-neutrality:allow doc-comment
 * cada marcação (ADR-0056 §2.2 + ADR-0058 §2.5.bis). Adotar move a marcação para // ux-neutrality:allow doc-comment
 * a camada capturável — só entra no laudo o que o humano adotou. // ux-neutrality:allow doc-comment
 */

export interface CaptureRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function intersectRects(a: CaptureRect, b: CaptureRect): CaptureRect | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  if (right <= x || bottom <= y) return null;
  return { x, y, width: right - x, height: bottom - y };
}

export function unionRects(a: CaptureRect, b: CaptureRect): CaptureRect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * Caixa que a imagem natural (`natW`×`natH`) ocupa dentro de `box` sob
 * `object-fit: contain` (centralizada, proporção preservada — o letterbox).
 *
 * E40.F1: `ctx.drawImage(img, …, w, h)` IGNORA o `object-fit` do CSS e estica a
 * imagem natural para preencher o destino. As `.signature-img` da mesa usam
 * `object-contain` e a E37.F3 forçou `h-full` nelas (elemento alto, assinatura
 * pequena no meio) — desenhar no `rect` do elemento esticava a assinatura. Aqui
 * devolvemos o retângulo do conteúdo real, usado como destino do draw e como
 * base do enquadramento. Sem dimensões naturais (imagem não carregada), devolve
 * `box` (comportamento anterior).
 */
export function objectContainRect(natW: number, natH: number, box: CaptureRect): CaptureRect {
  if (natW <= 0 || natH <= 0) return box;
  const scale = Math.min(box.width / natW, box.height / natH);
  const width = natW * scale;
  const height = natH * scale;
  return {
    x: box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  };
}

function toCaptureRect(rect: DOMRect): CaptureRect {
  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
}

/**
 * Recorte efetivo do elemento: interseção do root com cada ancestral que
 * recorta o próprio conteúdo (overflow ≠ visible) — é o que faz o conteúdo
 * ampliado pelo zoom (E36.F2) não vazar do painel na captura, igual à tela.
 */
export function clipRectFor(el: Element, root: HTMLElement): CaptureRect | null {
  let clip: CaptureRect | null = toCaptureRect(root.getBoundingClientRect());
  let node = el.parentElement;
  while (node !== null && clip !== null) {
    const overflow = window.getComputedStyle(node).overflow;
    if (overflow !== '' && overflow !== 'visible') {
      clip = intersectRects(clip, toCaptureRect(node.getBoundingClientRect()));
    }
    if (node === root) break;
    node = node.parentElement;
  }
  return clip;
}

export interface MesaDrawable {
  el: HTMLImageElement | HTMLCanvasElement | SVGSVGElement;
  /** Posição do elemento relativa ao root (pós-transform de zoom/pan). */
  rect: CaptureRect;
  /** Recorte relativo ao root (viewports com overflow hidden). */
  clip: CaptureRect;
  /** CSS filter computado do elemento (ex.: invert(1) com cores invertidas). */
  filter: string;
}

function isDrawableVisible(el: Element): boolean {
  if (el instanceof HTMLImageElement && (!el.complete || el.naturalWidth === 0)) return false;
  // Handles de seleção do Fabric não pertencem ao laudo.
  if (el.classList.contains('upper-canvas')) return false;
  // Marcações da triagem (E47.F3): apoio visual da raia isolada, nunca material
  // do documento pericial. A camada é marcada `capture-exclude` e o perito só a
  // faz entrar no laudo ADOTANDO a marcação — o que a move para a camada das
  // anotações dele. A checagem é no ancestral também porque a exclusão vale para
  // a camada inteira, não só para o nó que casar a classe.
  if (el.closest('.capture-exclude') !== null) return false;
  // SVG aninhado: o root SVG já rasteriza os filhos.
  if (el instanceof SVGSVGElement && el.parentElement?.closest('svg') !== null) return false;
  return true;
}

/** Elementos visuais do viewport, em ordem de documento (= ordem de pintura). */
export function collectMesaDrawables(root: HTMLElement): MesaDrawable[] {
  const rootRect = toCaptureRect(root.getBoundingClientRect());
  const drawables: MesaDrawable[] = [];
  for (const el of Array.from(root.querySelectorAll('img, canvas, svg'))) {
    if (!isDrawableVisible(el)) continue;
    const rect = toCaptureRect(el.getBoundingClientRect());
    if (rect.width <= 0 || rect.height <= 0) continue;
    const clipAbs = clipRectFor(el, root);
    if (clipAbs === null) continue;
    const visible = intersectRects(clipAbs, rect);
    if (visible === null) continue;
    drawables.push({
      el: el as MesaDrawable['el'],
      rect: {
        x: rect.x - rootRect.x,
        y: rect.y - rootRect.y,
        width: rect.width,
        height: rect.height,
      },
      clip: {
        x: clipAbs.x - rootRect.x,
        y: clipAbs.y - rootRect.y,
        width: clipAbs.width,
        height: clipAbs.height,
      },
      filter: window.getComputedStyle(el).filter,
    });
  }
  return drawables;
}

/**
 * Retângulo do conteúdo realmente pintado de um drawable. Para `<img>`, é o
 * letterbox do `object-contain` (a assinatura natural, sem o esticamento que o
 * `drawImage` causaria ao preencher o elemento alto da E37.F3). Para `<canvas>`
 * (bitmap preenche o elemento) e SVG (rasterizado no tamanho do rect), é o
 * próprio `rect`.
 */
export function drawableContentRect(drawable: MesaDrawable): CaptureRect {
  if (drawable.el instanceof HTMLImageElement) {
    return objectContainRect(drawable.el.naturalWidth, drawable.el.naturalHeight, drawable.rect);
  }
  return drawable.rect;
}

/**
 * Enquadramento do conteúdo: união dos retângulos VISÍVEIS (conteúdo ∩ clip) só
 * dos drawables RASTER substantivos — as imagens (Comparar) e o canvas Fabric da
 * mesa (Sobrepor & alinhar). Overlays SVG (grade, régua, marcadores, anotações)
 * cobrem o viewport inteiro (`absolute inset-0`) e NÃO entram no enquadramento —
 * senão a grade always-on faria o recorte ser o viewport todo. Eles são pintados,
 * mas recortados a esta caixa pela própria composição.
 *
 * E40.F1: a captura recortava o viewport inteiro do `.contested-viewport`, que a
 * E37.F3 tornou alto (`h-clamp(28rem, 100vh-15rem, 64rem)`). Com largura fixa de
 * 80mm no laudo, isso esticava a vista verticalmente e encolhia a assinatura.
 * Recortar no conteúdo (já no letterbox do `object-contain`) devolve o aspect
 * ratio fiel da assinatura.
 *
 * `null` quando não há conteúdo raster (ex.: imagens ainda não carregadas) — o
 * caller recai no viewport inteiro (comportamento anterior, defensivo).
 */
export function contentBounds(drawables: MesaDrawable[]): CaptureRect | null {
  let bounds: CaptureRect | null = null;
  for (const drawable of drawables) {
    if (!(drawable.el instanceof HTMLImageElement || drawable.el instanceof HTMLCanvasElement)) {
      continue;
    }
    const visible = intersectRects(drawableContentRect(drawable), drawable.clip);
    if (visible === null) continue;
    bounds = bounds === null ? visible : unionRects(bounds, visible);
  }
  return bounds;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('svg rasterization failed'));
    img.src = url;
  });
}

/** Rasteriza um overlay SVG no tamanho de render corrente (viewBox escala). */
async function svgToImage(svg: SVGSVGElement, rect: CaptureRect): Promise<HTMLImageElement> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(Math.max(1, Math.round(rect.width))));
  clone.setAttribute('height', String(Math.max(1, Math.round(rect.height))));
  const markup = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));
  try {
    return await loadImage(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

const CAPTURE_BACKGROUND = '#ffffff';

/** Margem (px de tela) ao redor do conteúdo recortado, para a vista respirar. */
const CAPTURE_MARGIN = 16;

/** Acresce margem ao recorte, saturando nos limites do viewport (`maxW`/`maxH`). */
export function padRect(r: CaptureRect, margin: number, maxW: number, maxH: number): CaptureRect {
  const x = Math.max(0, r.x - margin);
  const y = Math.max(0, r.y - margin);
  const right = Math.min(maxW, r.x + r.width + margin);
  const bottom = Math.min(maxH, r.y + r.height + margin);
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * Exporta a vista corrente da mesa como PNG, RECORTADA no conteúdo (E40.F1).
 *
 * O recorte é a caixa do conteúdo raster (assinaturas/canvas) + margem, não o
 * viewport inteiro — ver `contentBounds`. Sem conteúdo, recai no viewport todo.
 *
 * Levanta `Error` quando o ambiente não sustenta a composição (sem canvas 2D)
 * — o caller exibe o aviso de falha de captura.
 */
export async function captureMesaView(root: HTMLElement): Promise<Blob> {
  const rootRect = root.getBoundingClientRect();
  const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
  const drawables = collectMesaDrawables(root);
  const content = contentBounds(drawables);
  const crop =
    content === null
      ? { x: 0, y: 0, width: rootRect.width, height: rootRect.height }
      : padRect(content, CAPTURE_MARGIN, rootRect.width, rootRect.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(crop.width * dpr));
  canvas.height = Math.max(1, Math.round(crop.height * dpr));
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('canvas 2d unavailable');
  ctx.scale(dpr, dpr);
  // Composição em coordenadas relativas ao root; o translate ancora o recorte
  // na origem do canvas, então os draws e clips (todos root-relativos) seguem.
  ctx.translate(-crop.x, -crop.y);
  ctx.fillStyle = CAPTURE_BACKGROUND;
  ctx.fillRect(crop.x, crop.y, crop.width, crop.height);

  for (const drawable of drawables) {
    const source =
      drawable.el instanceof SVGSVGElement
        ? await svgToImage(drawable.el, drawable.rect)
        : drawable.el;
    // Imagens desenham no letterbox do object-contain (sem esticar); canvas/SVG
    // no próprio rect. O clip continua o viewport (zoom/pan), não o conteúdo.
    const dest = drawableContentRect(drawable);
    ctx.save();
    ctx.beginPath();
    ctx.rect(drawable.clip.x, drawable.clip.y, drawable.clip.width, drawable.clip.height);
    ctx.clip();
    if (drawable.filter !== '' && drawable.filter !== 'none') ctx.filter = drawable.filter;
    ctx.drawImage(source, dest.x, dest.y, dest.width, dest.height);
    ctx.restore();
  }

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob !== null ? resolve(blob) : reject(new Error('png export failed'))),
      'image/png',
    );
  });
}
